import { Component, inject, input, OnDestroy, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { FormGroup, FormArray, Validators, ReactiveFormsModule, FormControl } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { finalize } from 'rxjs/operators';
import { AuthService } from '../../../../../services/auth-service';
import { Router } from '@angular/router';
import { UserActivateDTO } from '../../../../../services/auth-service.model';
import { Notification } from '../../../../shared/notification/notification';

@Component({
  selector: 'app-otp-verify-sms',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, Notification],
  templateUrl: './otp-verify-sms.html',
  styleUrl: './otp-verify-sms.scss',
})
export class OtpVerifySms implements OnInit, OnDestroy {
  fromPage = input.required<string>();
  private authService = inject(AuthService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  errorMessage = signal<string | null>(null);
  showResend = signal<boolean>(false);
  phoneNumber = signal<string>('');
  resendLoading = signal<boolean>(false);
  showNotification = signal<boolean>(false);
  resendCooldown = signal<number>(0);
  private cooldownInterval: ReturnType<typeof setInterval> | null = null;

  otpForm = new FormGroup({
    digits: new FormArray<FormControl<string>>(
      Array(6)
        .fill('')
        .map(
          () =>
            new FormControl('', {
              nonNullable: true,
              validators: [Validators.required, Validators.pattern(/^[0-9]$/)],
            }),
        ),
    ),
  });

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.showNotification.set(true);
      setTimeout(() => this.showNotification.set(false), 3000);
      this.startCooldown();
      if (this.fromPage() == 'register' || sessionStorage.getItem('fromLogin')) {
        this.phoneNumber.set(localStorage.getItem('registeredMobile') || '');
      } else if (this.fromPage() == 'forgot-password') {
        this.phoneNumber.set(localStorage.getItem('forgotUsername') || '');
      }
      if (sessionStorage.getItem('fromLogin')) {
        this.resendOtp();
      }
    }
  }

  get digits(): FormArray<FormControl<string>> {
    return this.otpForm.get('digits') as FormArray<FormControl<string>>;
  }

  onInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    if (!/^\d$/.test(input.value)) {
      input.value = '';
      return;
    }
    if (input.value && index < 5) {
      const next = document.getElementById(`otp-${index + 1}`);
      next?.focus();
    }

    if (index === 5 && this.otpForm.valid) {
      this.submitOtp();
    }
  }

  submitOtp() {
    if (this.otpForm.invalid) {
      return;
    }
    this.showResend.set(false);
    this.errorMessage.set(null);
    if (this.fromPage() == 'register') {
      this.sendActivationOtp();
    } else if (this.fromPage() == 'forgot-password') {
      this.sendForgotPasswordOtp();
    }
  }

  resendOtp() {
    if (this.resendCooldown() > 0) {
      return;
    }
    this.errorMessage.set(null);
    this.showResend.set(false);
    this.resendLoading.set(true);
    if (this.fromPage() == 'register' || sessionStorage.getItem('fromLogin')) {
      this.resendActivationOtp();
    } else if (this.fromPage() == 'forgot-password') {
      this.resendForgotPasswordOtp();
    }
  }

  sendActivationOtp() {
    const otpValue = this.digits.value.join('');
    const payload: UserActivateDTO = { otp: otpValue };
    this.authService.activateUserAccount(this.phoneNumber(), payload).subscribe({
      next: () => {
        if (isPlatformBrowser(this.platformId)) {
          localStorage.removeItem('registeredMobile');
          localStorage.removeItem('otpMethod');
          localStorage.removeItem('registeredEmail');
          localStorage.removeItem('registerForm');
          localStorage.setItem('activationSuccess', 'true');
          this.router.navigate(['/register/verification-success'], { replaceUrl: true });
        }
      },
      error: (err) => {
        console.error('Activation OTP verification failed:', err);
        this.errorMessage.set(err.messageEn || 'An unexpected error occurred. Please try again later.');
        if (err.code === '0021') {
          this.showResend.set(true);
        } else {
          this.showResend.set(false);
        }
        this.digits.reset();
        const firstInput = document.getElementById('otp-0');
        firstInput?.focus();
      },
    });
  }

  resendActivationOtp() {
    this.authService
      .resendActivationOtp(this.phoneNumber())
      .pipe(finalize(() => this.resendLoading.set(false)))
      .subscribe({
        next: () => {
          console.log('Activation OTP resent successfully');
          this.showNotification.set(true);
          setTimeout(() => this.showNotification.set(false), 3000);
          this.startCooldown();
        },
        error: (err) => {
          console.error('Failed to resend Activation OTP:', err);
          this.errorMessage.set(err.messageEn || 'An unexpected error occurred. Please try again later.');
        },
      });
  }

  sendForgotPasswordOtp() {
    const otpValue = this.digits.value.join('');
    const payload: UserActivateDTO = { otp: otpValue };
    this.authService.verifyPasswordResetOtp(this.phoneNumber(), payload).subscribe({
      next: () => {
        if (isPlatformBrowser(this.platformId)) {
          localStorage.setItem('forgotUsername', this.phoneNumber());
          localStorage.setItem('resetOtp', otpValue);
          this.router.navigate(['/reset-password'], { replaceUrl: true });
        }
      },
      error: (err) => {
        console.error('Forgot Password OTP verification failed:', err);
        this.errorMessage.set(err.messageEn || 'An unexpected error occurred. Please try again later.');
        if (err.code === '0021') {
          this.showResend.set(true);
        } else {
          this.showResend.set(false);
        }

        this.digits.reset();
        const firstInput = document.getElementById('otp-0');
        firstInput?.focus();
      },
    });
  }

  resendForgotPasswordOtp() {
    this.authService
      .requestPasswordReset(this.phoneNumber(), { method: 'SMS' })
      .pipe(finalize(() => this.resendLoading.set(false)))
      .subscribe({
        next: () => {
          console.log('Forgot Password OTP resent successfully');
          this.showNotification.set(true);
          setTimeout(() => this.showNotification.set(false), 3000);
          this.startCooldown();
        },
        error: (err) => {
          console.error('Failed to resend Forgot Password OTP:', err);
          this.errorMessage.set(err.messageEn || 'An unexpected error occurred. Please try again later.');
        },
      });
  }

  startCooldown() {
    this.resendCooldown.set(60);
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }
    this.cooldownInterval = setInterval(() => {
      const current = this.resendCooldown();
      if (current > 0) {
        this.resendCooldown.set(current - 1);
      } else {
        if (this.cooldownInterval) {
          clearInterval(this.cooldownInterval);
        }
        this.cooldownInterval = null;
      }
    }, 1000);
  }

  ngOnDestroy() {
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }
  }

  private errorCodeToMessage(code: string): string {
    console.log('Error code:', code);
    if (code === '0020') {
      return 'Activation OTP is incorrect. Please try again.';
    } else {
      return 'An unexpected error occurred. Please try again later.';
    }
  }
}
